import React, { useState, useEffect } from 'react';
import InventoryAPI from '../../services/inventoryService';

// ProductPicker — reusable retail-product search/add panel for checkout flows.
// Loads the location's sellable stock (retail_price > 0, on-hand > 0) and lets
// the user search and add a product. Presentational w.r.t. the *effect* of
// adding — it calls `onAdd(product, qty)` and the caller does the billing
// (job-attached → /requestService/custom; standalone register → product_sales).
//
// Reused by: the FINISHED checkout drawer (job-attached upsell) and the
// standalone register (NewSaleDrawer).
//
// Props:
//   installerId   number
//   locationId    string|number
//   tier          'retail' | 'wholesale'  (which price column to show; default retail)
//   busyId        product_id currently being added (disables its row), optional
//   onAdd(product, qty)  — product: the stock row; qty: integer
//   onClose()            — optional back/close

const fmt = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;

const priceFor = (p, tier) => parseFloat(tier === 'wholesale' ? p.wholesale_price : p.retail_price) || 0;
const onHandOf = (p) =>
  parseFloat(p.quantity_on_hand ?? p.total_quantity ?? p.quantity_in_stock ?? p.stock_quantity ?? p.quantity) || 0;

const ProductPicker = ({ installerId, locationId, tier = 'retail', busyId = null, onAdd, onClose }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await InventoryAPI.getStockLevels(installerId, {}, locationId);
        if (cancelled) return;
        // Sellable at this tier = price > 0 and something on hand.
        const sellable = (rows || []).filter((p) => priceFor(p, tier) > 0 && onHandOf(p) > 0);
        // Stock rows are per-product-per-location; collapse to one row per
        // product (summing on-hand) so an unscoped "all" view doesn't dupe.
        const byId = new Map();
        for (const p of sellable) {
          const id = p.product_id || p.id;
          if (byId.has(id)) {
            byId.get(id).quantity_on_hand = (byId.get(id).quantity_on_hand || 0) + onHandOf(p);
          } else {
            byId.set(id, { ...p, quantity_on_hand: onHandOf(p) });
          }
        }
        setProducts([...byId.values()]);
      } catch (err) {
        if (!cancelled) setError('Could not load products.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [installerId, locationId, tier]);

  const q = query.trim().toLowerCase();
  const results = !q
    ? products
    : products.filter((p) => {
        const hay = `${p.product_name || p.name || ''} ${p.brand || ''} ${p.sku || ''}`.toLowerCase();
        return hay.includes(q);
      });

  return (
    <div className="product-picker">
      <div className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
        {onClose && (
          <button type="button" className="btn btn-link p-0" onClick={onClose} aria-label="Back">
            <i className="fa-sharp fa-regular fa-arrow-left" style={{ color: '#6B7280' }} />
          </button>
        )}
        <div className="input-group">
          <div className="input-group-prepend"><span className="input-group-text"><i className="fa-sharp fa-regular fa-magnifying-glass" /></span></div>
          <input
            type="text"
            className="form-control"
            placeholder="Search product, brand, or SKU"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>
      </div>

      {loading && (
        <div className="text-center text-muted py-3" style={{ fontSize: 13 }}>
          <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" /> Loading products…
        </div>
      )}

      {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

      {!loading && !error && results.length === 0 && (
        <div className="text-center text-muted py-3" style={{ fontSize: 13 }}>
          {products.length === 0 ? 'No retail products in stock at this location.' : 'No matches.'}
        </div>
      )}

      {!loading && !error && results.map((p) => {
        const id = p.product_id || p.id;
        const price = priceFor(p, tier);
        const stock = onHandOf(p);
        const busy = busyId != null && String(busyId) === String(id);
        return (
          <div key={id} className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: '1px solid #F1F1F4' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>{p.product_name || p.name}</div>
              <div style={{ fontSize: 12, color: '#9CA3AF' }}>
                {[p.brand, `${stock} in stock`].filter(Boolean).join(' · ')}
              </div>
            </div>
            <div className="d-flex align-items-center" style={{ gap: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>{fmt(price)}</span>
              <button
                type="button"
                className="btn btn-sm"
                style={{ border: '1px solid #3FB2BE', color: '#3FB2BE', borderRadius: 9999, fontWeight: 600 }}
                onClick={() => onAdd && onAdd(p, 1)}
                disabled={busy}
              >
                {busy ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> : (<><i className="fa-sharp fa-regular fa-plus mr-1" /> Add</>)}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProductPicker;
